import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { PageOrientation, PageSize, TDocumentDefinitions } from 'pdfmake/interfaces';
import { imagesForPdf } from '../components/Icons/itu-base64';

const styles: TDocumentDefinitions['styles'] = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 16],
  },
  surveyTitle: {
    fontSize: 12,
  },
  chapter: {
    fontSize: 16,
    bold: true,
    margin: [0, 16, 0, 10],
  },
  section: {
    fontSize: 12,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  question: {
    fontSize: 9,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  answer: {
    fontSize: 9,
    margin: [2, 0, 0, 0],
  },
  listItem: {
    margin: [0, 0, 0, 3],
  },
  none: {},
  tableHeader: {
    bold: true,
    fontSize: 9,
  },
  tableBody: {
    fontSize: 9,
  },
};

export class PdfService {
  constructContent({
    survey,
    organization,
    progress,
    updatedAt,
    organizationQuestionAnswers,
    organizationTabularAnswers,
  }: {
    survey: any;
    organization?: any;
    progress?: string;
    updatedAt?: string;
    organizationTabularAnswers?: any;
    organizationQuestionAnswers?: any;
  }): { fileName: string; content: TDocumentDefinitions['content']; images: TDocumentDefinitions['images'] } {
    const { title, fileName } = this.buildFileName(survey);

    const content: TDocumentDefinitions['content'] = [];

    const headerContent = this.constructHeader({ title, organization, progress, updatedAt });
    content.push(headerContent);

    let counter1 = 1;
    let counter2 = 1;
    let counter3 = 1;

    for (const chapter of this.sortByOrder(survey.chapters)) {
      const text = `${counter1}. ${chapter.name}`;
      content.push({ text, style: 'chapter' });

      for (const section of this.sortByOrder(chapter.sections)) {
        const text = `${counter1}.${counter2}. ${section.name}`;
        content.push({ text, style: 'section' });

        for (const fieldBlock of this.sortByOrder(section.fieldBlocks)) {
          const { id, sectionId, question, fieldType, options, hasOtherOption, table, matrice: matrix } = fieldBlock;
          const text = `${counter1}.${counter2}.${counter3++}. ${question}`;

          if (!['Table', 'Matrix'].includes(fieldType)) {
            content.push({ text, style: 'question' });
          }

          switch (fieldType) {
            case 'Free Text':
              const textAnswer = organizationQuestionAnswers?.find(
                (el: { questionId: string; answer: string }) => el.questionId === id,
              )?.answer;
              if (textAnswer) {
                content.push({ text: textAnswer, style: 'answer' });
              } else {
                content.push({ text: '__________________________________________________', style: 'answer' });
              }
              break;

            case 'Checkbox':
            case 'Radio':
              const isRadio = fieldType === 'Radio';
              for (const option of this.sortByOrder(options)) {
                const checkboxAnswer = organizationQuestionAnswers?.find(
                  el => el.questionId === id && el.optionId === option.id,
                );
                const checkbox = this.checkboxCanvas(!!checkboxAnswer, isRadio) as any;
                content.push({
                  columns: [checkbox, { text: option.name, style: 'answer' }],
                  columnGap: 6,
                  style: 'listItem',
                });
              }

              if (hasOtherOption) {
                const otherAnswer = organizationQuestionAnswers?.findLast(el => el.questionId === id && el.answer);
                const checkbox = this.checkboxCanvas(!!otherAnswer, isRadio) as any;
                const otherAnswerFinal = `Other: ${otherAnswer?.answer ?? '____________________'}`;

                content.push({
                  columns: [checkbox, { text: otherAnswerFinal, style: 'answer' }],
                  columnGap: 6,
                  style: 'listItem',
                });
              }
              break;

            case 'Table':
            case 'Matrix':
              const tableBody = [];

              const isMatrix = fieldType === 'Matrix';
              const tableObj = isMatrix ? matrix : table;
              const rows = this.sortByOrder(isMatrix ? tableObj?.rows : tableObj?.organizationRows) ?? [];

              const answers =
                organizationTabularAnswers?.filter((el: { sectionId: string }) => el.sectionId === sectionId) || [];

              const { tableHeaders, columnIds, cellTypeIsCheckbox, tableHasOtherOption } = this.extractColumnHeaders(
                tableObj,
                isMatrix,
              );

              if (!isMatrix && !answers?.length) {
                const emptyRows = Array(5)
                  .fill(0)
                  .map(() =>
                    columnIds.map(() =>
                      cellTypeIsCheckbox
                        ? this.checkboxCanvas(false, !cellTypeIsCheckbox)
                        : { text: ' ', style: 'tableBody' },
                    ),
                  );
                tableBody.push(...emptyRows);
              } else {
                for (const { name, id } of rows) {
                  const row = columnIds.map((columnId: string | null, i) => {
                    if (!columnId) return { text: name || ' ', style: 'tableBody' };

                    const isLastColumn = i === columnIds.length - 1;

                    const rowIdKey = isMatrix ? 'rowId' : 'organizationRowId';

                    const cellAnswer = answers.find(
                      (el: { rowId: string; columnId: string; organizationRowId: string }) =>
                        id === el[rowIdKey] && el.columnId === columnId,
                    );

                    if (isMatrix || cellTypeIsCheckbox) {
                      if (isMatrix && isLastColumn && tableHasOtherOption) {
                        // skip checkbox to print plain text instead
                      } else {
                        return this.checkboxCanvas(cellAnswer?.checked, !cellTypeIsCheckbox);
                      }
                    }

                    return { text: cellAnswer?.answer || ' ', style: 'tableBody' };
                  });
                  tableBody.push(row);
                }
              }

              content.push({
                style: 'table',
                table: {
                  ...(!organizationTabularAnswers && {
                    widths: columnIds.map(() => '*'),
                  }),
                  body: [tableHeaders, ...tableBody],
                },
              });
              break;

            default:
              content.push({ text: '...', style: 'answer' });
          }
        }
        counter2++;
        counter3 = 1;
      }
      counter1++;
      counter2 = 1;
    }

    return { fileName, content, images: imagesForPdf };
  }

  constructHeader({
    title,
    organization,
    progress,
    updatedAt,
  }: {
    title: string;
    organization?: { name?: string; country?: { name?: string } };
    progress?: string;
    updatedAt: string;
  }): TDocumentDefinitions['content'] {
    const rows: [string, string][] = [];

    const { name: organizationName, country } = organization ?? {};
    const countryName = country?.name;

    rows.push(['Survey:', title]);
    if (countryName) rows.push(['Country:', countryName]);
    if (organizationName) rows.push(['Administration:', organizationName]);
    if (progress) {
      rows.push(['Progress:', progress]);
      if (progress === 'Submitted') rows.push(['Date submitted:', updatedAt]);
    }

    const exportDate = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    rows.push(['Export date:', exportDate]);

    const headerContent: TDocumentDefinitions['content'] = [
      { image: 'itu-logo.svg', width: 50, margin: [45, 0, 0, 0] },
      {
        table: {
          widths: [100, '*'],
          body: rows.map(([key, value], i) => [
            { text: key, style: i ? 'tableHeader' : 'surveyTitle', alignment: 'right', margin: [0, 2, 8, 2] },
            { text: value, style: i ? 'tableBody' : 'surveyTitle', alignment: 'left', margin: [0, 2, 0, 2] },
          ]),
        },
        layout: 'noBorders',
        margin: [0, 10, 0, 15],
      },
    ];

    return headerContent;
  }

  triggerDownload(
    {
      fileName,
      content,
      images,
    }: {
      fileName: string;
      content: TDocumentDefinitions['content'];
      images: TDocumentDefinitions['images'];
    },
    pageSize: PageSize = 'A4',
    pageOrientation: PageOrientation = 'portrait',
  ) {
    pdfMake
      .createPdf(
        {
          content,
          styles,
          images,
          pageOrientation,
          pageSize,
          footer: (currentPage, pageCount) => ({
            text: `${currentPage}/${pageCount}`,
            alignment: 'right',
            margin: [0, 10, 20, 0],
            fontSize: 9,
            color: 'darkgray',
          }),
        },
        undefined,
        undefined,
        pdfFonts.vfs,
      )
      .download(fileName);
  }

  // Helpers

  private simplify(text: string): string {
    return text.toLowerCase().replace(/ /g, '-');
  }

  private buildFileName({ name, surveySeriesName }: { name: string | null; surveySeriesName: string | null }): {
    title: string;
    fileName: string;
  } {
    const date = new Date().toISOString().split('T')[0];

    let title = surveySeriesName || 'Survey';
    const parts = [date];
    if (surveySeriesName) {
      parts.push(this.simplify(surveySeriesName));
    }
    if (name) {
      parts.push(this.simplify(name));
      title += ` (${name})`;
    }

    const fileName = `${parts.join('-')}.pdf`;

    return { title, fileName };
  }

  private extractColumnHeaders(
    table: { columns: Array<{ name: string; id: string; type?: 'Radio' | 'Checkbox'; order: number }> },
    isMatrix?: boolean,
  ) {
    const tableHeaders = [];
    const columnIds = [];

    const cellTypeIsCheckbox = table?.columns?.[0]?.type === 'Checkbox';

    if (isMatrix) {
      tableHeaders.push({ text: ' ', style: 'tableHeader' });
      columnIds.push(null);
    }

    const columnsSorted = this.sortByOrder(table?.columns);

    for (const column of columnsSorted) {
      tableHeaders.push({ text: column.name, style: 'tableHeader' });
      columnIds.push(column.id);
    }
    // TODO NOW handle other option for matrix

    const tableHasOtherOption = columnsSorted.at(-1)?.type === 'Free Text';

    return { tableHeaders, columnIds, cellTypeIsCheckbox, tableHasOtherOption };
  }

  private sortByOrder<T extends { order: number }[]>(arr: T) {
    if (!arr?.length) return [];
    const sorted = arr.sort((a, b) => a.order - b.order);
    return sorted;
  }

  // Canvas drawings

  private checkboxCanvas(checked: boolean, isRadio?: boolean) {
    const size = 8;
    const canvas: any[] = [];

    if (isRadio) {
      canvas.push({
        type: 'ellipse',
        style: 'none',
        x: size / 2,
        y: size / 2,
        r1: size / 2,
        r2: size / 2,
        lineWidth: 0.8,
      });
    } else {
      canvas.push({ type: 'rect', style: 'none', x: 0, y: 0, w: size, h: size, r: 1, lineWidth: 0.8 });
    }
    if (checked) {
      if (isRadio) {
        canvas.push({
          type: 'ellipse',
          style: 'none',
          x: size / 2,
          y: size / 2,
          r1: size / 3,
          r2: size / 3,
          color: 'black',
        });
      } else {
        canvas.push(
          { type: 'line', style: 'none', x1: 2, y1: 4, x2: 3.5, y2: 6, lineWidth: 1.2 },
          { type: 'line', style: 'none', x1: 3.5, y1: 6, x2: 6, y2: 2.5, lineWidth: 1.2 },
        );
      }
    }
    return { canvas, width: size, height: size, margin: [0, 1, 2, 0] };
  }
}

export const pdfService = new PdfService();
